"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();
import type {StaticImageData} from "next/image";
import type React from "react";
import {useEffect, useRef, useState} from "react";
import {Media} from "@/components/Media";
import type {Header} from "@/payload-types";
import "./index.scss";
import MegaMenu from "@/components/Header/MegaMenu";
import HeaderSearch from "@/components/Header/Search";
import Icon from "@/components/Icon";
import {CMSLink} from "@/components/Link";
import LocaleSwitcher from "@/components/LocaleSwitcher";
import {Link} from "@/i18n/routing";

interface HeaderClientProps {
	data: Header;
	staticImage?: StaticImageData;
}

export const HeaderClient: React.FC<HeaderClientProps> = ({
	data,
	staticImage,
}) => {
	const { logo, secondaryMenu, primaryMenu } = data;

	const [openMegaMenuIndex, setOpenMegaMenuIndex] = useState<number | null>(
		null,
	);
	const [isCompact, setIsCompact] = useState(false);
	const [megaMenuTop, setMegaMenuTop] = useState(0);

	// Ref for mega menu hover delay
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const primaryMenuRef = useRef<HTMLDivElement>(null);

	// Refs for smooth scroll handling
	const lastScrollRef = useRef(0);
	const tickingRef = useRef(false);

	// Smooth scroll handling with throttle and hysteresis
	useEffect(() => {
		// Initialize on mount
		const initialScroll =
			window.pageYOffset || document.documentElement.scrollTop;
		lastScrollRef.current = initialScroll;

		const handleScroll = () => {
			const scrollTop =
				window.pageYOffset || document.documentElement.scrollTop;

			if (!tickingRef.current) {
				window.requestAnimationFrame(() => {
					const SCROLL_DOWN_THRESHOLD = 50; // Become compact when scrolling down past this
					const SCROLL_UP_THRESHOLD = 30; // Return to normal when at top
					const MIN_SCROLL_DELTA = 5; // Minimum scroll distance to register direction change

					const scrollDelta = scrollTop - lastScrollRef.current;
					const scrollingDown = scrollDelta > MIN_SCROLL_DELTA;
					const scrollingUp = scrollDelta < -MIN_SCROLL_DELTA;

					// At top (near 0) - always show full header
					if (scrollTop <= SCROLL_UP_THRESHOLD) {
						if (isCompact) {
							setIsCompact(false);
						}
					}
					// Scrolling down past threshold - compact
					else if (
						scrollingDown &&
						scrollTop > SCROLL_DOWN_THRESHOLD &&
						!isCompact
					) {
						setIsCompact(true);
					}
					// Scrolling up while below top threshold - show full header
					else if (scrollingUp && isCompact) {
						setIsCompact(false);
					}

					lastScrollRef.current = scrollTop;
					tickingRef.current = false;
				});

				tickingRef.current = true;
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [isCompact]);

	// Cleanup mega menu timeout on unmount
	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		};
	}, []);

	const handleMegaMenuMouseEnter = (index: number) => {
		// Clear any pending close timeout
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		// Calculate mega menu position
		if (primaryMenuRef.current) {
			const rect = primaryMenuRef.current.getBoundingClientRect();
			setMegaMenuTop(rect.bottom);
		}
		setOpenMegaMenuIndex(index);
	};

	const handleMegaMenuMouseLeave = () => {
		// Delay closing to allow smooth transition to dropdown
		closeTimeoutRef.current = setTimeout(() => {
			setOpenMegaMenuIndex(null);
			closeTimeoutRef.current = null;
		}, 150); // 150ms allows smooth mouse movement
	};

	const handleMegaMenuClose = () => {
		// Immediately close mega menu (used for clicks)
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setOpenMegaMenuIndex(null);
	};

	const handleClick = () => {
		const menu = document.getElementById("nav");
		const OpenButton = document.getElementById("openMenu");
		if (menu && OpenButton) {
			if (menu.style.display === "flex") {
				menu.style.display = "none";
				OpenButton.style.display = "flex";
			} else {
				menu.style.display = "flex";
				OpenButton.style.display = "none";
			}
		}
	};

	return (
		<header id="header" className={isCompact ? "is-compact" : ""}>
			{/*<Container>*/}
			<Link href="/">
				{logo && (
					<Media className="header_logo" src={staticImage} resource={logo} priority />
				)}
			</Link>
			<div className="header__nav">
				<div className="header__menu header__secondary-menu">
					{secondaryMenu?.map(({ link }, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
						<CMSLink key={index} {...link} />
					))}
					<LocaleSwitcher />
				</div>
				<div ref={primaryMenuRef} className="header__menu header__primary-menu">
					{primaryMenu?.map((link, index) => (
						// biome-ignore lint/a11y/noStaticElementInteractions: <explanation>
						// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
						<div
							key={link.id}
							onMouseEnter={() =>
								link.isMegaMenu && handleMegaMenuMouseEnter(index)
							}
							onMouseLeave={() => link.isMegaMenu && handleMegaMenuMouseLeave()}
						>
							<div className="header__menu-item">
								<CMSLink {...link.link} />
								{link.isMegaMenu && <Icon name="chevronDown" />}
							</div>
						</div>
					))}
					<div className="header__search">
						<HeaderSearch />
					</div>
				</div>
			</div>
			{/* Mega Menu - rendered at header level for full width */}
			{primaryMenu?.map((link, index) => (
				link.isMegaMenu && link.subMenu && (
					<MegaMenu
						key={link.id}
						links={link.subMenu}
						isOpen={openMegaMenuIndex === index}
						closeMenu={handleMegaMenuClose}
						top={megaMenuTop}
						onMouseEnter={() => handleMegaMenuMouseEnter(index)}
						onMouseLeave={handleMegaMenuMouseLeave}
					/>
				)
			))}
			<Link href="#" id="openMenu" onClick={handleClick}>
				{/** biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
					/>
				</svg>
			</Link>
			<div id="nav" className="navigation-mobile">
				<div className="navigation-mobile__off-canvas">
					<nav className="menu__navigation">
						<div className="menu__navigation_title">
							<p>Menu</p>
							<Link href="#" id="closeMenu" onClick={handleClick}>
								{/** biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={2}
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M6 18 18 6M6 6l12 12"
									/>
								</svg>
							</Link>
						</div>
						<div className="menu__navigation-links">
							{primaryMenu?.map(({ link }, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								<CMSLink key={index} {...link} onClick={handleClick} />
							))}
							{secondaryMenu?.map(({ link }, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								<CMSLink key={index} {...link} onClick={handleClick} />
							))}
						</div>
						<LocaleSwitcher inline />
					</nav>
				</div>
			</div>
			{/*</Container>*/}
		</header>
	);
};
